import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import cx from 'classnames';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import Gallery from '../Gallery/Gallery';
import s from './HistoryLogsBlock.module.css';
import { useIsMounted } from '../../UseIsMounted';
import { Loader } from '../../Loader/Loader';
import { useTranslation } from 'react-i18next';

const Log = ({ log }) => {
    const tooltip = log.user ? `${log.user.name} <br/> ${log.user.email}` : '';

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [tooltip]);

    return (
        <div className={s.actionItem}>
            <div className={s.user}>
                {log.user && (
                    <img
                        src={log.user.avatar_url}
                        data-tip={tooltip}
                        className={s.userPhoto}
                        data-multiline
                    />
                )}
                {!log.user && <div className={cx(s.userPhoto, s.deleteUserPhoto)} />}
                <span className={s.actionDate}>
                    {moment(log.datetime).format('DD.MM.YYYY, HH:mm')}
                </span>
            </div>
            <div className={s.actionInfoBlock}>
                <div className={s.actionDescript}>
                    {log.text}
                    {log.photos && (
                        <div className={s.actionPhotoWrapper}>
                            <Gallery photos={log.photos} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line no-underscore-dangle
function HistoryLogsBlock_({ guid }, forwardedRef) {
    const { t } = useTranslation();
    const isMounted = useIsMounted();
    const MIN_ROW_HEIGHT = 30;
    const LOAD_LOGS_IN_ONE_REQUEST_COUNT = 50;
    const url = helpable.replaceUrlParameters(routable.getRoute('loadInventoryLogs'), {
        _guid_: guid,
    });
    const [itemCount, setItemCount] = useState(0);
    const listRef = useRef(null);
    const itemsRef = useRef([]);
    const [isLoadItem, setIsLoadItemCount] = useState(false);

    const addLog = useCallback(
        (log) => {
            itemsRef.current.unshift(log);
            setItemCount(itemCount + 1);
            if (listRef.current) {
                // eslint-disable-next-line no-underscore-dangle
                listRef.current._listRef.resetAfterIndex(0);
            }
        },
        [itemsRef, itemCount, setItemCount, listRef]
    );

    useImperativeHandle(
        forwardedRef,
        () => ({
            addLog,
        }),
        [addLog]
    );

    const loadingRequests = useRef({});

    const isItemLoaded = useCallback((index) => !!itemsRef.current[index], [itemsRef]);

    const getItemSize = useCallback(
        (index) => {
            const domString = renderToString(<Row index={index} style={{ width: 604 }} />);
            const div = document.createElement('div');
            div.innerHTML = domString;
            document.body.appendChild(div);
            const { height } = div.getBoundingClientRect();
            document.body.removeChild(div);

            return Math.max(height, MIN_ROW_HEIGHT);
        },
        [itemsRef]
    );

    const Row = ({ index, style }) => {
        const rowRef = useRef(null);
        const newStyle = { ...style };
        let content;

        if (!isItemLoaded(index)) {
            content = t('HistoryLogsBlock.loaderText');
        } else {
            content = <Log log={itemsRef.current[index]} />;
        }

        return (
            <div data-id={`history-log-${index}`} ref={rowRef} style={newStyle}>
                {content}
            </div>
        );
    };

    const loadMoreItems = useCallback(
        (visibleStartIndex) => {
            const requestUrl = `${url}?page=${
                Math.floor(visibleStartIndex / LOAD_LOGS_IN_ONE_REQUEST_COUNT) + 1
            }&count_per_page=${LOAD_LOGS_IN_ONE_REQUEST_COUNT}`;
            if (loadingRequests.current[requestUrl]) {
                return loadingRequests.current[requestUrl];
            }
            const promise = fetch(requestUrl)
                .then((response) => response.json())
                .then((data) => {
                    itemsRef.current.push(...data.response.items);
                    if (isMounted.current) {
                        setItemCount(data.response.pagination.logs_count);
                        setIsLoadItemCount(true);
                    }
                });
            loadingRequests.current[requestUrl] = promise;

            return promise;
        },
        [itemsRef, url]
    );

    useEffect(() => {
        loadMoreItems(0);
    }, []);

    return (
        <>
            {isLoadItem && (
                <InfiniteLoader
                    ref={listRef}
                    isItemLoaded={isItemLoaded}
                    loadMoreItems={loadMoreItems}
                    itemCount={itemCount}
                >
                    {({ onItemsRendered, ref }) => (
                        <List
                            onItemsRendered={onItemsRendered}
                            ref={ref}
                            height={120}
                            itemSize={getItemSize}
                            itemCount={itemCount}
                        >
                            {Row}
                        </List>
                    )}
                </InfiniteLoader>
            )}
            {!isLoadItem && <Loader size="mediumDot" color="grayDot" loaderWidth="loaderWidth40" />}
        </>
    );
}

export const HistoryLogsBlock = React.forwardRef(HistoryLogsBlock_);
